import React, { useState, useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight, Star } from "lucide-react";
import { ResourceCard } from "../ResourceCard/ResourceCard";
import { useIsMobile } from "@/hooks/use-mobile";
import { useFeaturedResources } from "@/modules/hub/features/ResourceLibrary/hooks/useFeaturedResources";

interface FeaturedResourcesProps {
  onViewAllFeatured: () => void;
  context?: 'library' | 'podcasts';
}

export function FeaturedResources({
  onViewAllFeatured,
  context = 'library',
}: FeaturedResourcesProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [cardsToShow, setCardsToShow] = useState(1); // Start with 1 to prevent layout shift
  const containerRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const { featuredResources, isLoading, error } = useFeaturedResources({
    context,
  });

  // Dynamically calculate how many cards to show based on container width
  useEffect(() => {
    const calculateCardsToShow = () => {
      if (!containerRef.current) return;

      const containerWidth = containerRef.current.offsetWidth;

      // With sidebar (320px) and gaps, we need to be more aggressive about dropping to fewer cards
      if (isMobile || containerWidth < 400) {
        setCardsToShow(1);
      } else if (containerWidth < 700) {
        setCardsToShow(2);
      } else {
        setCardsToShow(3);
      }
    };

    // Initial calculation with a small delay to ensure DOM is ready
    const timeoutId = setTimeout(calculateCardsToShow, 0);

    // Also calculate on window resize as a fallback
    window.addEventListener("resize", calculateCardsToShow);

    // Create ResizeObserver to watch for container size changes
    const resizeObserver = new ResizeObserver(() => {
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(calculateCardsToShow);
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", calculateCardsToShow);
      resizeObserver.disconnect();
    };
  }, [isMobile]);

  // Also recalculate when featuredResources load
  useEffect(() => {
    if (featuredResources.length > 0 && containerRef.current) {
      const containerWidth = containerRef.current.offsetWidth;

      if (isMobile || containerWidth < 400) {
        setCardsToShow(1);
      } else if (containerWidth < 700) {
        setCardsToShow(2);
      } else {
        setCardsToShow(3);
      }
    }
  }, [featuredResources, isMobile]);

  const maxIndex = Math.max(0, featuredResources.length - cardsToShow);

  // Reset currentIndex when cardsToShow changes to prevent out of bounds
  useEffect(() => {
    setCurrentIndex((prevIndex) => Math.min(prevIndex, maxIndex));
  }, [maxIndex]);

  const handlePrevious = () => {
    setCurrentIndex(Math.max(0, currentIndex - cardsToShow));
  };

  const handleNext = () => {
    setCurrentIndex(Math.min(maxIndex, currentIndex + cardsToShow));
  };

  if (isLoading) {
    return (
      <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950 dark:to-indigo-950 rounded-xl">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Star className="h-5 w-5 text-yellow-500 fill-current" />
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              Featured Resources
            </h2>
          </div>
        </div>
        <div
          className={`grid gap-4 ${cardsToShow === 3 ? "grid-cols-3" : cardsToShow === 2 ? "grid-cols-2" : "grid-cols-1"}`}
        >
          {Array.from({ length: cardsToShow }).map((_, i) => (
            <div className="animate-pulse" key={i}>
              <div className="bg-gray-200 dark:bg-gray-700 rounded-lg h-40"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error || featuredResources.length === 0) {
    return null;
  }

  const visibleResources = featuredResources.slice(
    currentIndex,
    currentIndex + cardsToShow,
  );

  return (
    <div
      className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950 dark:to-indigo-950 rounded-xl"
      ref={containerRef}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Star className="h-5 w-5 text-yellow-500 fill-current" />
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            Featured Resources
          </h2>
          <span className="text-sm text-gray-600 dark:text-gray-400">
            ({featuredResources.length} total)
          </span>
        </div>

        {featuredResources.length > cardsToShow && (
          <div className="flex items-center gap-2">
            <button
              aria-label="Previous featured resources"
              className="p-1.5 rounded-full bg-white dark:bg-gray-800 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              disabled={currentIndex === 0}
              onClick={handlePrevious}
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              aria-label="Next featured resources"
              className="p-1.5 rounded-full bg-white dark:bg-gray-800 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              disabled={currentIndex >= maxIndex}
              onClick={handleNext}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      <div className="relative">
        <div
          className={`grid gap-4 ${
            cardsToShow === 3
              ? "grid-cols-3"
              : cardsToShow === 2
                ? "grid-cols-2"
                : "grid-cols-1"
          }`}
        >
          {visibleResources.map((resource) => (
            <div
              className="transform transition-transform hover:scale-[1.02]"
              key={resource.id}
            >
              <ResourceCard
                resource={resource}
                showFeaturedBadge={true}
                variant="featured"
              />
            </div>
          ))}
        </div>

        {/* Pagination dots for mobile or single card view */}
        {cardsToShow === 1 && featuredResources.length > 1 && (
          <div className="flex justify-center gap-1.5 mt-3">
            {featuredResources.map((_, index) => (
              <button
                aria-label={`Go to featured resource ${index + 1}`}
                className={`h-1.5 w-1.5 rounded-full transition-all ${
                  index === currentIndex
                    ? "bg-blue-600 dark:bg-blue-400 w-4"
                    : "bg-gray-300 dark:bg-gray-600"
                }`}
                key={index}
                onClick={() => setCurrentIndex(index)}
              />
            ))}
          </div>
        )}
      </div>

      {/* View all featured button */}
      {featuredResources.length > cardsToShow && (
        <div className="mt-4 text-center">
          <button
            className="text-sm text-blue-600 dark:text-blue-400 hover:underline font-medium"
            onClick={onViewAllFeatured}
          >
            View all {featuredResources.length} featured resources →
          </button>
        </div>
      )}
    </div>
  );
}
